/**
 * Field Configuration Interfaces
 * Định nghĩa cấu trúc config cho từng field
 */

import { ValidatorFn, AsyncValidatorFn } from '@angular/forms';
import { VisibilityCondition } from './form-schema.interface';

/**
 * Field Configuration Interface
 * Define individual form field
 */
export interface FieldConfig {
    /** Unique field key (maps to form control name) */
    key: string;

    /** Field type */
    type: FieldType;

    /** Display label */
    label?: string;

    /** Placeholder text */
    placeholder?: string;

    /** Helper text below field */
    hint?: string;

    /** Default value */
    defaultValue?: any;

    /** Validation configuration */
    validation?: ValidationConfig;

    /** Layout for this field */
    layout?: FieldLayout;

    /** Field options (for select, radio, checkbox group) */
    options?: FieldOption[] | (() => Promise<FieldOption[]>);

    /** Conditional visibility */
    visible?: VisibilityCondition | boolean;

    /** Disabled state */
    disabled?: boolean | DisabledCondition;

    /** Readonly state */
    readonly?: boolean;

    /** Field-specific attributes */
    attributes?: FieldAttributes;

    /** Custom CSS class */
    cssClass?: string;

    /** Event handlers */
    events?: FieldEvents;

    /** Dependencies on other fields */
    dependencies?: FieldDependency[];
}

/**
 * Supported Field Types
 */
export type FieldType =
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'tel'
    | 'url'
    | 'textarea'
    | 'select'
    | 'multi-select'
    | 'checkbox'
    | 'checkbox-group'
    | 'radio-group'
    | 'date'
    | 'datetime'
    | 'date-range'
    | 'file-upload'
    | 'autocomplete'
    | 'switch'
    | 'slider'
    | 'editor'
    | 'custom';

/**
 * Validation Configuration
 */
export interface ValidationConfig {
    /** Required validator */
    required?: boolean | { value: boolean; message?: string };

    /** Min length validator */
    minLength?: number | { value: number; message?: string };

    /** Max length validator */
    maxLength?: number | { value: number; message?: string };

    /** Min value validator */
    min?: number | { value: number; message?: string };

    /** Max value validator */
    max?: number | { value: number; message?: string };

    /** Email validator */
    email?: boolean | { value: boolean; message?: string };

    /** Pattern/Regex validator */
    pattern?: string | RegExp | { value: string | RegExp; message?: string };

    /** Custom sync validators */
    customValidators?: ValidatorFn[];

    /** Async validators (e.g., server-side validation) */
    asyncValidators?: AsyncValidatorFn[];

    /** Custom validation messages */
    messages?: ValidationMessages;

    /** Preset validation patterns */
    preset?: PresetValidation;
}

/**
 * Preset Validation Patterns
 */
export type PresetValidation =
    | 'mobilephone'    // Số điện thoại VN (10-11 số, bắt đầu bằng 0)
    | 'email'          // Email format
    | 'url'            // URL format
    | 'taxcode'        // Mã số thuế
    | 'license'        // CMND/CCCD (12 số)
    | 'password'       // Password mạnh
    | 'decimal'        // Số thập phân
    | 'positive'       // Số dương
    | 'integer';       // Số nguyên

/**
 * Validation Error Messages
 */
export interface ValidationMessages {
    required?: string;
    minLength?: string;
    maxLength?: string;
    min?: string;
    max?: string;
    email?: string;
    pattern?: string;
    mobilephone?: string;
    url?: string;
    taxcode?: string;
    license?: string;
    password?: string;
    decimal?: string;
    custom?: { [key: string]: string };
}

/**
 * Field Layout
 */
export interface FieldLayout {
    /** Column span (1-12) */
    colSpan?: number;

    /** Row span */
    rowSpan?: number;

    /** Order */
    order?: number;

    /** Hide label */
    hideLabel?: boolean;
}

/**
 * Field Option (for select, radio, checkbox-group)
 */
export interface FieldOption {
    label: string;
    value: any;
    disabled?: boolean;
    icon?: string;
    description?: string;
    cssClass?: string;
}

/**
 * Field Attributes
 * HTML attributes for input element
 */
export interface FieldAttributes {
    autocomplete?: string;
    autofocus?: boolean;
    multiple?: boolean;
    accept?: string;
    step?: number;
    rows?: number;
    cols?: number;
    [key: string]: any;
}

/**
 * Field Events
 */
export interface FieldEvents {
    onChange?: (value: any, formValue: any) => void;
    onBlur?: (value: any) => void;
    onFocus?: (value: any) => void;
    onClick?: (event: any) => void;
    onKeyup?: (event: any) => void;
}

/**
 * Disabled Condition
 */
export interface DisabledCondition {
    field: string;
    operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';
    value: any;
}

/**
 * Field Dependency
 * Khi field A thay đổi → update options/value của field B
 */
export interface FieldDependency {
    /** Source field to watch */
    sourceField: string;

    /** Action to perform */
    action: 'updateOptions' | 'updateValue' | 'setDisabled' | 'setVisible';

    /** Function to calculate new value */
    transform?: (sourceValue: any, currentValue: any) => any;
}
